import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { ArrowRight, type LucideIcon } from 'lucide-react'
import { InteractiveDots, Pressable, ScrollReveal, TextReveal, useCompactViewport } from '@/components/motion'
import { DocThumb, type DocKind } from './DocThumb'
import { BRAND, OUT_EASE, halftone } from './halftone'

export interface ScrollCard {
  title: string
  description: string
  icon: LucideIcon
}

const FAN_ACCENTS = ['blue', 'purple', 'pink', 'purple'] as const

/**
 * Blue "scroll cards" band: a sticky left column (heading, copy, pink
 * button, a fan of skewed document thumbnails) while white cards scroll up
 * the right column, starting a screen down. The halftone is held sticky
 * behind both. Below lg it is one column in normal flow.
 */
export function ScrollCards({
  heading,
  body,
  cta,
  documents,
  cards,
}: {
  heading: string
  body: string
  cta: { label: string; href: string }
  documents: Array<{ kind: DocKind; title: string }>
  cards: ScrollCard[]
}) {
  const compact = useCompactViewport()
  return (
    <section className="relative overflow-x-clip bg-[var(--color-landing-accent)] text-white">
      {/* Halftone held in view while the band scrolls past */}
      <div aria-hidden className="pointer-events-none sticky top-0 -mb-[100svh] h-[100svh]">
        <InteractiveDots />
      </div>

      <div className="landing-container landing-section-lg relative z-10 grid gap-16 lg:grid-cols-[1.25fr_1fr] lg:gap-20">
        <div className="self-start lg:sticky lg:top-28">
          <TextReveal as="h2" className="landing-text-display-xl max-w-[18ch] text-white">
            {heading}
          </TextReveal>
          <ScrollReveal delay={0.15}>
            <p className="landing-text-body-lg mt-8 max-w-[52ch] text-pretty text-white/85">{body}</p>
          </ScrollReveal>
          <ScrollReveal delay={0.25} className="mt-10">
            <Pressable className="inline-block">
              <Link
                to={cta.href}
                className="landing-cta landing-cta--solid landing-cta--lg landing-cta--on-color landing-btn-sweep border-[#ff00f2] bg-[#ff00f2] text-2xl dark:border-[#ff00f2] dark:bg-[#ff00f2] dark:text-white lg:text-2xl"
              >
                {cta.label}
                <ArrowRight className="h-5 w-5" />
              </Link>
            </Pressable>
          </ScrollReveal>

          {/* Fan of document thumbnails, bleeding off the left edge */}
          <div aria-hidden className="relative mt-14 h-[13rem] md:h-[15rem]">
            {documents.slice(0, 4).map((doc, i) => (
              <motion.div
                key={doc.title}
                className="absolute top-0 w-[7.5rem] md:w-[9.5rem]"
                style={{ left: `calc(${i} * min(24%, 8.25rem) - 2.5rem)` }}
                initial={compact ? false : { opacity: 0, x: -40 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true, margin: '0px 0px -10% 0px' }}
                transition={{ duration: 0.8, ease: OUT_EASE, delay: 0.2 + i * 0.08 }}
              >
                <div
                  className="shadow-[0_24px_40px_-20px_rgba(0,0,0,0.55)]"
                  style={{ transform: `perspective(900px) rotateY(${-12 - i * 5}deg) skewY(${-4 + i}deg) translateY(${-i * 0.6}rem)` }}
                >
                  <DocThumb kind={doc.kind} title={doc.title} accent={FAN_ACCENTS[i]} className="aspect-[3/4] w-full" />
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        <ul className="space-y-6 md:space-y-8 lg:pt-[75svh]">
          {cards.map((card, i) => {
            const Icon = card.icon
            return (
              <li key={card.title}>
                <ScrollReveal
                  distance={60}
                  className="flex min-h-[18rem] flex-col justify-between gap-12 bg-white p-8 text-[var(--color-landing-primary)] md:min-h-[22rem] md:p-10 dark:bg-[#12121a] dark:text-white"
                >
                  <span
                    aria-hidden
                    className="flex h-14 w-14 items-center justify-center text-white"
                    style={{ backgroundColor: i === 1 ? BRAND.pink : BRAND.blue, ...halftone(0.28, 5) }}
                  >
                    <Icon className="h-7 w-7" strokeWidth={1.75} />
                  </span>
                  <div>
                    <h3 className="font-[family-name:var(--font-landing-display)] text-xl uppercase leading-[1.4] tracking-[-0.01em] md:text-2xl">
                      {card.title}
                    </h3>
                    <p className="landing-text-body mt-4 max-w-[40ch] text-pretty text-[var(--color-landing-gray-600)] dark:text-[var(--color-landing-gray-400)]">
                      {card.description}
                    </p>
                  </div>
                </ScrollReveal>
              </li>
            )
          })}
        </ul>
      </div>
    </section>
  )
}

export default ScrollCards
